// Tier 6 marketing block renderers.
//
// Kept out of `renderers.tsx` (already 3k lines) but exported through it, so
// every surface still imports from one place. Each renderer here follows the
// same three rules the rest of the runtime does:
//
//   1. Pure and SSR-safe — no browser APIs at render time, so
//      `renderToStaticMarkup` produces the complete markup.
//   2. Degrade to visible — anything interactive is authored as working
//      markup first (a real link, a real range input, a real checkbox) and
//      only enhanced by a shared script.
//   3. Theme tokens, never hardcoded colors.

import React, { useContext, useState, type CSSProperties, type ReactNode } from 'react'
import type {
  Align, BeforeAfterBlock, BentoGridBlock, CarouselBlock, ComparisonTableBlock,
  CookieConsentBlock, CounterBlock, FloatingButtonBlock, LottieBlock, MarqueeBlock, SocialLinksBlock,
  SocialNetwork, StepsBlock, TeamBlock,
} from '@twasta/page-schema'
import { PageIcon } from './iconMap'
import { Check } from 'lucide-react'
import { aspectRatioCss, ITEM_CLASS } from './theme'
import {
  PageButton, backgroundStyle, responsiveGrid, useSiteLink,
  imgLoadingProps, CountValue, SlideCarousel, usePageVarsSub, PageVarsContext,
} from './renderers'

// ─── Section header (eyebrow / heading / subheading) ──────────────────
// Six of these blocks share the same header; one component keeps their
// typography identical and routes all three lines through the theme tokens.
function SectionHead({ eyebrow, heading, subheading, align = 'left' }: {
  eyebrow?: string; heading?: string; subheading?: string; align?: Align
}) {
  const s = usePageVarsSub()
  if (!eyebrow && !heading && !subheading) return null
  return (
    <header style={{ textAlign: align, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {eyebrow && <span className="pg-eyebrow">{s(eyebrow)}</span>}
      {heading && (
        <h2 style={{ margin: 0, fontSize: '1.7rem', fontWeight: 800, color: 'var(--pg-heading, var(--pg-text))' }}>
          {s(heading)}
        </h2>
      )}
      {subheading && (
        <p style={{
          margin: 0, fontSize: '0.95rem', color: 'var(--pg-muted, var(--pg-text-secondary))',
          maxWidth: 640,
          alignSelf: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
        }}>{s(subheading)}</p>
      )}
    </header>
  )
}

// ─── Bento grid ───────────────────────────────────────────────────────

/**
 * The asymmetric tile grid that is the current default layout idiom for
 * product pages. Cells declare a column/row span on a 3- or 4-track desktop
 * grid; the shared CSS collapses that to 2 tracks on tablet and 1 on mobile,
 * where an author's spans would otherwise overflow the viewport.
 */
export function BentoGridRenderer({ block }: { block: BentoGridBlock }) {
  const s = usePageVarsSub()
  const cols = block.columns || 4
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <SectionHead eyebrow={block.eyebrow} heading={block.heading} subheading={block.subheading} align="left" />
      <div
        className="pg-bento"
        style={{
          ['--pg-bento-cols' as never]: String(cols),
          ['--pg-bento-gap' as never]: `${block.gap ?? 16}px`,
        }}
      >
        {(block.cells || []).map((c) => {
          const span = Math.min(c.span || 1, cols)
          return (
            <div
              key={c.id}
              className={`pg-bento-cell ${ITEM_CLASS}`}
              // `data-wide` is what the tablet breakpoint keys off to keep a
              // deliberately-wide tile wide at 2 tracks instead of snapping
              // everything to one column.
              data-wide={span >= 3 ? '' : undefined}
              style={{
                gridColumn: `span ${span}`,
                gridRow: `span ${c.rowSpan || 1}`,
                textAlign: c.align || 'left',
                ...backgroundStyle(c.background),
              }}
            >
              {c.imageUrl && (
                <img
                  src={c.imageUrl} alt=""
                  {...imgLoadingProps(false)}
                  style={{
                    position: 'absolute', inset: 0, width: '100%', height: '100%',
                    objectFit: 'cover', opacity: 0.22, pointerEvents: 'none',
                  }}
                />
              )}
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
                {c.icon && <PageIcon name={c.icon} size={26} color="var(--pg-accent)" />}
                {c.eyebrow && <span className="pg-eyebrow">{s(c.eyebrow)}</span>}
                {c.title && (
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>
                    {s(c.title)}
                  </h3>
                )}
                {c.body && (
                  <p style={{ margin: 0, fontSize: '0.88rem', lineHeight: 1.55, color: 'var(--pg-text)', flex: 1 }}>
                    {s(c.body)}
                  </p>
                )}
                {c.cta && <div style={{ marginTop: 'auto' }}><PageButton btn={c.cta} /></div>}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

// ─── Marquee ──────────────────────────────────────────────────────────

/**
 * A continuously scrolling strip of text and/or logos.
 *
 * Pure CSS keyframes, so it runs on the published static site. The track
 * holds one copy at render time; `marqueeDuplicate` clones it (aria-hidden)
 * so the -50% translate loops seamlessly without doubling the content a
 * crawler or screen reader sees. Under `prefers-reduced-motion` the CSS
 * hides the clone and wraps the row — the items are all still there.
 */
export function MarqueeRenderer({ block }: { block: MarqueeBlock }) {
  const s = usePageVarsSub()
  const { resolveHref } = useSiteLink()
  const h = block.itemHeight ?? 32
  return (
    <div
      className={[
        'pg-marquee',
        block.direction === 'right' ? 'pg-marquee-rev' : '',
        block.pauseOnHover !== false ? 'pg-marquee-pause' : '',
        block.fadeEdges !== false ? 'pg-marquee-fade' : '',
      ].filter(Boolean).join(' ')}
      style={{ ['--pg-mq-speed' as never]: `${block.speed ?? 30}s` }}
    >
      <div className="pg-marquee-track">
        {(block.items || []).map((it) => {
          const body = it.imageUrl
            ? <img src={it.imageUrl} alt={it.text || ''} {...imgLoadingProps(false)} style={{ height: h, width: 'auto', display: 'block' }} />
            : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {s(it.text || '')}
                {block.separator ? <span aria-hidden style={{ opacity: 0.4 }}>{block.separator}</span> : null}
              </span>
            )
          return it.href
            ? <a key={it.id} href={resolveHref(it.href)} style={{ color: 'inherit', textDecoration: 'none' }}>{body}</a>
            : <React.Fragment key={it.id}>{body}</React.Fragment>
        })}
      </div>
    </div>
  )
}

// ─── Counter ──────────────────────────────────────────────────────────

/**
 * Animated count-up figures. The final value is in the DOM at render time,
 * so this degrades to a plain (correct) stat row for a crawler, a
 * reduced-motion visitor, or a browser without IntersectionObserver.
 */
export function CounterRenderer({ block }: { block: CounterBlock }) {
  const s = usePageVarsSub()
  const align = block.align || 'center'
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {block.heading && (
        <h2 style={{ margin: 0, textAlign: align, fontSize: '1.4rem', fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>
          {s(block.heading)}
        </h2>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: responsiveGrid(block.columns || 4, 140, 16), gap: 16 }}>
        {(block.items || []).map((it) => (
          <div key={it.id} className={ITEM_CLASS} style={{ textAlign: align, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: '2.4rem', fontWeight: 800, color: 'var(--pg-primary)', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              {it.prefix ? <span>{s(it.prefix)}</span> : null}
              <CountValue
                value={it.value}
                duration={block.duration}
                decimals={it.decimals}
                separator={block.separator !== false}
              />
              {it.suffix ? <span>{s(it.suffix)}</span> : null}
            </div>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--pg-text)' }}>{s(it.label)}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

// ─── Before / after ───────────────────────────────────────────────────

/**
 * Draggable comparison between two images.
 *
 * The divider position lives in a real `<input type="range">`, so the
 * control is fully usable — keyboard included — with no JavaScript at all.
 * `beforeAfterScript` only adds click-and-drag anywhere on the image.
 */
export function BeforeAfterRenderer({ block }: { block: BeforeAfterBlock }) {
  const s = usePageVarsSub()
  const initial = Math.max(0, Math.min(100, block.initial ?? 50))
  const ratio = aspectRatioCss(block.aspect || '16:9')
  const vertical = block.orientation === 'vertical'
  return (
    <figure style={{ margin: 0 }}>
      <div
        className="pg-ba"
        data-orient={vertical ? 'vertical' : undefined}
        style={{ ['--pg-ba' as never]: `${initial}%`, aspectRatio: ratio }}
      >
        <img src={block.beforeUrl} alt={block.beforeLabel || 'Before'} {...imgLoadingProps(false)} />
        <div className="pg-ba-after">
          <img src={block.afterUrl} alt={block.afterLabel || 'After'} {...imgLoadingProps(false)} />
        </div>
        <div className="pg-ba-handle" aria-hidden><span className="pg-ba-knob">{vertical ? '↕' : '↔'}</span></div>
        {block.beforeLabel && <span className="pg-ba-label pg-ba-label-b">{s(block.beforeLabel)}</span>}
        {block.afterLabel && <span className="pg-ba-label pg-ba-label-a">{s(block.afterLabel)}</span>}
        <input
          className="pg-ba-range"
          type="range" min={0} max={100} defaultValue={initial}
          aria-label="Comparison position"
        />
      </div>
      {block.caption && (
        <figcaption style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--pg-text-secondary)', textAlign: 'center' }}>
          {s(block.caption)}
        </figcaption>
      )}
    </figure>
  )
}

// ─── Carousel ─────────────────────────────────────────────────────────

/** A general-purpose slide carousel (previously only `hero` could hold slides). */
export function CarouselRenderer({ block }: { block: CarouselBlock }) {
  const s = usePageVarsSub()
  const slides = block.slides || []
  if (!slides.length) return null
  const height = block.height ?? 380
  return (
    <SlideCarousel
      id={block.id}
      autoplay={block.autoplay !== false}
      interval={block.autoplayInterval ?? 5000}
      count={slides.length}
      minHeight={height}
      // Unset ⇒ shown, matching the schema defaults and the behaviour before
      // these were wired through. `perView` is not honoured yet: slides
      // cross-fade on one stacked track, so showing several at once needs a
      // different track layout (CSS + carouselScript), not a prop.
      showDots={block.showDots !== false}
      showArrows={block.showArrows !== false}
    >
      {slides.map((sl) => (
        <div
          key={sl.id}
          style={{
            position: 'relative', minHeight: height, display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 32,
            ...backgroundStyle(sl.background),
          }}
        >
          {sl.imageUrl && (
            <img
              src={sl.imageUrl} alt=""
              {...imgLoadingProps(false)}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
          {(sl.title || sl.body || sl.cta) && (
            <div style={{
              position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column',
              gap: 12, alignItems: 'center', textAlign: 'center', maxWidth: 640,
              // A scrim only where there is text over an image, so a
              // picture-only slide isn't needlessly darkened.
              ...(sl.imageUrl ? { background: 'rgba(6,10,20,.42)', color: '#fff', padding: 28, borderRadius: 14 } : {}),
            }}>
              {sl.title && <h3 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800 }}>{s(sl.title)}</h3>}
              {sl.body && <p style={{ margin: 0, lineHeight: 1.55 }}>{s(sl.body)}</p>}
              {sl.cta && <PageButton btn={sl.cta} />}
            </div>
          )}
        </div>
      ))}
    </SlideCarousel>
  )
}

// ─── Team ─────────────────────────────────────────────────────────────

export function TeamRenderer({ block }: { block: TeamBlock }) {
  const s = usePageVarsSub()
  const shape = block.shape || 'circle'
  return (
    <section className={`pg-team-${shape}`} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <SectionHead eyebrow={block.eyebrow} heading={block.heading} subheading={block.subheading} align={block.align || 'left'} />
      <div style={{ display: 'grid', gridTemplateColumns: responsiveGrid(block.columns || 4, 200, 20), gap: 20 }}>
        {(block.members || []).map((m) => (
          <div key={m.id} className={ITEM_CLASS} style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: block.align || 'left' }}>
            {m.photoUrl
              ? <img className="pg-team-photo" src={m.photoUrl} alt={m.name} {...imgLoadingProps(false)} />
              : (
                <div className="pg-team-photo" style={{
                  aspectRatio: '1 / 1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '2rem', fontWeight: 800, color: 'var(--pg-muted)',
                }}>{(m.name || '?').slice(0, 1).toUpperCase()}</div>
              )}
            <div>
              <div style={{ fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>{s(m.name)}</div>
              {m.role && <div style={{ fontSize: '0.82rem', color: 'var(--pg-muted, var(--pg-text-secondary))' }}>{s(m.role)}</div>}
            </div>
            {m.bio && <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.55, color: 'var(--pg-text)' }}>{s(m.bio)}</p>}
            {m.links?.length ? <SocialRow links={m.links} iconSize={18} variant="plain" /> : null}
          </div>
        ))}
      </div>
    </section>
  )
}

// ─── Steps / process ──────────────────────────────────────────────────

/** The progress step a page has reached: `current`, moved on by the last
 *  `advance` rule whose page variable holds a value. */
export function progressStep(block: StepsBlock, vars: unknown): number {
  let current = Math.max(0, block.current ?? 0)
  const read = (k: string): unknown => typeof vars === 'function'
    ? (vars as (key: string) => unknown)(k)
    : (vars && typeof vars === 'object' ? (vars as Record<string, unknown>)[k] : undefined)
  for (const r of block.advance || []) {
    if (!r || !r.whenVar) continue
    const v = read(r.whenVar)
    if (v !== undefined && v !== null && String(v).trim() !== '') current = Math.max(0, Number(r.current) || 0)
  }
  return current
}

export function StepsRenderer({ block }: { block: StepsBlock }) {
  const s = usePageVarsSub()
  const vars = useContext(PageVarsContext)
  const horizontal = (block.layout || 'horizontal') === 'horizontal'
  if (block.variant === 'progress') {
    const current = progressStep(block, vars)
    const justify = block.align === 'center' ? 'center' : block.align === 'right' ? 'flex-end' : 'flex-start'
    return (
      <ol aria-label={block.heading ? s(block.heading) : 'Progress'} data-pg-steps="progress"
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap',
                   alignItems: 'center', justifyContent: justify, gap: 10, fontSize: '0.95rem', fontWeight: 600 }}>
        {(block.steps || []).map((st, i) => {
          const done = i < current
          const here = i === current
          return (
            <li key={st.id} aria-current={here ? 'step' : undefined}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              {i > 0 && <span aria-hidden style={{ color: 'var(--pg-muted, var(--pg-text-secondary))', fontWeight: 400 }}>—</span>}
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                color: done ? 'var(--pg-success, #16a34a)'
                  : here ? 'var(--pg-heading, var(--pg-text))'
                  : 'var(--pg-muted, var(--pg-text-secondary))',
              }}>
                {s(st.title)}
                {done && <Check size={15} strokeWidth={2.5} aria-label="done" />}
              </span>
            </li>
          )
        })}
      </ol>
    )
  }
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <SectionHead eyebrow={block.eyebrow} heading={block.heading} subheading={block.subheading} align={block.align || 'left'} />
      <ol className={`pg-steps ${horizontal ? 'pg-steps-horizontal' : ''}`} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {(block.steps || []).map((st, i) => (
          <li key={st.id} className={`pg-step ${ITEM_CLASS}`}>
            <span className="pg-step-num">
              {block.numbered === false && st.icon
                ? <PageIcon name={st.icon} size={18} color="var(--pg-on-primary, #fff)" />
                : i + 1}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>{s(st.title)}</div>
              {st.description && (
                <p style={{ margin: 0, fontSize: '0.86rem', lineHeight: 1.55, color: 'var(--pg-muted, var(--pg-text-secondary))' }}>
                  {s(st.description)}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

// ─── Comparison table ─────────────────────────────────────────────────

/**
 * Feature matrix. A boolean cell renders as a check/cross glyph with a
 * text alternative for assistive tech; anything else renders as text.
 * The wrapper scrolls horizontally rather than letting a wide matrix push
 * the whole page sideways.
 */
export function ComparisonTableRenderer({ block }: { block: ComparisonTableBlock }) {
  const s = usePageVarsSub()
  const cols = block.columns || []
  // Rows carrying a `group` open a full-width group header before them.
  let lastGroup: string | undefined
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHead heading={block.heading} subheading={block.subheading} align="center" />
      <div className="pg-cmp-wrap">
        <table className={`pg-cmp ${block.stickyHeader !== false ? 'pg-cmp-sticky' : ''}`}>
          <thead>
            <tr>
              <th />
              {cols.map((c) => (
                <th key={c.id} className={c.highlighted ? 'pg-cmp-hi' : undefined}>
                  {s(c.label)}
                  {c.badge ? (
                    <span style={{
                      display: 'block', marginTop: 4, fontSize: '0.62rem', fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--pg-primary)',
                    }}>{s(c.badge)}</span>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(block.rows || []).map((r) => {
              const openGroup = r.group && r.group !== lastGroup ? r.group : null
              lastGroup = r.group || lastGroup
              return (
                <React.Fragment key={r.id}>
                  {openGroup && (
                    <tr className="pg-cmp-group"><td colSpan={cols.length + 1}>{s(openGroup)}</td></tr>
                  )}
                  <tr className={ITEM_CLASS}>
                    <td>{s(r.label)}</td>
                    {cols.map((c, ci) => {
                      const v = r.values?.[ci]
                      return (
                        <td key={c.id} className={c.highlighted ? 'pg-cmp-hi' : undefined}>
                          {typeof v === 'boolean'
                            ? <span className={v ? 'pg-cmp-yes' : 'pg-cmp-no'}>
                                <PageIcon name={v ? 'check' : 'x'} size={18} />
                                <span style={SR_ONLY}>{v ? 'Included' : 'Not included'}</span>
                              </span>
                            : (v ? s(String(v)) : <span className="pg-cmp-no" aria-label="Not included">—</span>)}
                        </td>
                      )
                    })}
                  </tr>
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

const SR_ONLY: CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
}

// ─── Social links / share ─────────────────────────────────────────────

/** lucide icon name per network (falls back to a globe). */
const NETWORK_ICON: Record<SocialNetwork, string> = {
  x: 'twitter', facebook: 'facebook', instagram: 'instagram', linkedin: 'linkedin',
  youtube: 'youtube', github: 'github', tiktok: 'music', threads: 'message-circle',
  discord: 'message-circle', whatsapp: 'message-circle', telegram: 'send',
  mastodon: 'globe', pinterest: 'image', reddit: 'message-square',
  email: 'mail', website: 'globe', rss: 'rss',
}

const NETWORK_LABEL: Record<SocialNetwork, string> = {
  x: 'X', facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn',
  youtube: 'YouTube', github: 'GitHub', tiktok: 'TikTok', threads: 'Threads',
  discord: 'Discord', whatsapp: 'WhatsApp', telegram: 'Telegram',
  mastodon: 'Mastodon', pinterest: 'Pinterest', reddit: 'Reddit',
  email: 'Email', website: 'Website', rss: 'RSS',
}

function SocialRow({ links, iconSize, variant, align }: {
  links: { id: string; network: SocialNetwork; url: string; label?: string }[]
  iconSize: number
  variant: 'plain' | 'circle' | 'square'
  align?: Align
}) {
  return (
    <div
      className={`pg-social pg-social-${variant}`}
      style={{ justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start' }}
    >
      {links.map((l) => (
        <a
          key={l.id}
          className={ITEM_CLASS}
          href={l.url}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={l.label || NETWORK_LABEL[l.network] || l.network}
          title={l.label || NETWORK_LABEL[l.network]}
        >
          <PageIcon name={NETWORK_ICON[l.network] || 'globe'} size={iconSize} />
        </a>
      ))}
    </div>
  )
}

/**
 * Follow links, or share buttons for the current page.
 *
 * Share URLs are built at RENDER time from `window.location` where it
 * exists; on the static prerender there is no location, so the href is left
 * as the network's bare share endpoint and the browser fills in the URL from
 * the `u`/`url` parameter the script would otherwise set. Either way the
 * link is a real anchor, never a JS-only handler.
 */
export function SocialLinksRenderer({ block }: { block: SocialLinksBlock }) {
  const size = block.iconSize ?? 20
  const variant = block.variant || 'plain'

  if ((block.mode || 'follow') === 'follow') {
    return <SocialRow links={block.links || []} iconSize={size} variant={variant} align={block.align} />
  }

  const pageUrl = typeof window !== 'undefined' ? window.location.href : ''
  const title = block.shareTitle || (typeof document !== 'undefined' ? document.title : '')
  const nets = block.networks || ['x', 'facebook', 'linkedin', 'whatsapp', 'email']
  const links = nets.map((n) => ({
    id: n,
    network: n,
    url: shareUrl(n, pageUrl, title),
    label: `Share on ${NETWORK_LABEL[n] || n}`,
  }))
  return <SocialRow links={links} iconSize={size} variant={variant} align={block.align} />
}

function shareUrl(net: SocialNetwork, url: string, title: string): string {
  const u = encodeURIComponent(url)
  const t = encodeURIComponent(title)
  switch (net) {
    case 'x': return `https://twitter.com/intent/tweet?url=${u}&text=${t}`
    case 'facebook': return `https://www.facebook.com/sharer/sharer.php?u=${u}`
    case 'linkedin': return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`
    case 'whatsapp': return `https://wa.me/?text=${t}%20${u}`
    case 'telegram': return `https://t.me/share/url?url=${u}&text=${t}`
    case 'reddit': return `https://www.reddit.com/submit?url=${u}&title=${t}`
    case 'pinterest': return `https://pinterest.com/pin/create/button/?url=${u}&description=${t}`
    case 'email': return `mailto:?subject=${t}&body=${u}`
    default: return url || '#'
  }
}

// ─── Lottie / animated SVG ────────────────────────────────────────────

/**
 * An animated illustration.
 *
 * `lottie-player` is loaded lazily and ONLY on an interactive surface — the
 * static prerender and any reduced-motion visitor get `posterUrl` instead.
 * That keeps a 200 KB player off the critical path of a marketing page and
 * means the block never renders empty: the poster is the base state and the
 * animation is the enhancement.
 */
export function LottieRenderer({ block }: { block: LottieBlock }) {
  const s = usePageVarsSub()
  const height = block.height ?? 280
  const [ready, setReady] = useState(false)

  const reduce = typeof window !== 'undefined'
    && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  React.useEffect(() => {
    if (reduce) return
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    // An .svg needs no player at all — it animates itself in an <img>.
    if (/\.svg($|\?)/i.test(block.url)) { setReady(true); return }
    if (customElements.get('lottie-player')) { setReady(true); return }
    const id = 'pg-lottie-player'
    if (document.getElementById(id)) {
      customElements.whenDefined('lottie-player').then(() => setReady(true)).catch(() => {})
      return
    }
    const sc = document.createElement('script')
    sc.id = id
    sc.src = 'https://unpkg.com/@lottiefiles/lottie-player@2.0.8/dist/lottie-player.js'
    sc.async = true
    sc.onload = () => setReady(true)
    // A failed CDN load leaves the poster in place rather than an empty box.
    sc.onerror = () => setReady(false)
    document.head.appendChild(sc)
  }, [block.url, reduce])

  const poster = block.posterUrl
    ? <img src={block.posterUrl} alt="" {...imgLoadingProps(false)} style={{ height, width: '100%', objectFit: 'contain', display: 'block' }} />
    : <div style={{ height }} aria-hidden />

  const isSvg = /\.svg($|\?)/i.test(block.url)

  return (
    <figure style={{ margin: 0 }}>
      {!ready || reduce
        ? poster
        : isSvg
          ? <img src={block.url} alt="" style={{ height, width: '100%', objectFit: 'contain', display: 'block' }} />
          : React.createElement('lottie-player', {
              src: block.url,
              background: 'transparent',
              speed: '1',
              loop: block.loop !== false ? true : undefined,
              autoplay: block.autoplay !== false ? true : undefined,
              style: { height, width: '100%' },
            })}
      {block.caption && (
        <figcaption style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--pg-text-secondary)', textAlign: 'center' }}>
          {s(block.caption)}
        </figcaption>
      )}
    </figure>
  )
}

// ─── Floating button ──────────────────────────────────────────────────

/**
 * A call-to-action pinned to the viewport — an edge tab ("Book Demo") or a
 * corner pill. Pure CSS positioning plus the shared `PageButton`, so the
 * click (open a pop-up, go to a page, scroll to a form) behaves exactly like
 * every other button, including on the static site with no JS. Placed on
 * the site header chrome page it appears on every page.
 */
export function FloatingButtonRenderer({ block, inline = false }: { block: FloatingButtonBlock; inline?: boolean }) {
  const pos = block.position || 'bottom-right'
  const vertical = !!block.vertical && (pos === 'right-center' || pos === 'left-center')
  const cls = `pg-float-${String(block.id).replace(/[^A-Za-z0-9_-]/g, '')}`
  const place: Record<string, CSSProperties> = {
    'right-center': { right: 0, top: '50%', transform: vertical ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)' },
    'left-center': { left: 0, top: '50%', transform: 'translateY(-50%)' },
    'bottom-right': { right: 20, bottom: 20 },
    'bottom-left': { left: 20, bottom: 20 },
    'top-right': { right: 20, top: 90 },
  }
  const style: CSSProperties = inline
    ? { display: 'inline-block' }
    : {
        position: 'fixed', zIndex: 60, ...place[pos],
        ...(vertical ? { writingMode: 'vertical-rl' } : {}),
      }
  const vars = {
    ...(block.background ? { ['--pg-primary' as never]: block.background } : {}),
    ...(block.textColor ? { ['--pg-on-primary' as never]: block.textColor } : {}),
  } as CSSProperties
  const iconOnly = block.iconOnlyBelow && block.button?.icon && !inline
    // The label is hidden visually only, so the link keeps its accessible name.
    ? `@media (max-width:${block.iconOnlyBelow}px){.${cls} a>span{position:absolute!important;width:1px;height:1px;`
      + `overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}.${cls} a{padding:14px!important;border-radius:999px!important;gap:0!important}`
      + `.${cls} a svg{width:26px;height:26px}}`
    : ''
  const hide = (block.hideBelow && !inline
    ? `@media (max-width:${block.hideBelow}px){.${cls}{display:none!important}}`
    : '') + iconOnly
  return (
    <>
      {hide ? <style dangerouslySetInnerHTML={{ __html: hide }} /> : null}
      <div className={`pg-float ${cls}`} style={{ ...style, ...vars }}>
        <div style={vertical ? { borderRadius: pos === 'right-center' ? '0 8px 8px 0' : '0 8px 8px 0', overflow: 'hidden', ['--pg-button-radius' as never]: '0px' } : undefined}>
          <PageButton btn={block.button} />
        </div>
      </div>
    </>
  )
}

// ─── Cookie consent ───────────────────────────────────────────────────

/**
 * Consent banner.
 *
 * Rendered HIDDEN and revealed by `consentScript` only when no decision is
 * stored — a returning visitor never sees it flash. The same script is what
 * releases any analytics tag parked behind `requiresConsent`, so the
 * banner is genuinely load-bearing rather than decorative.
 */
export function CookieConsentRenderer({ block }: { block: CookieConsentBlock }) {
  const s = usePageVarsSub()
  const { resolveHref } = useSiteLink()
  const pos = block.position || 'bottom'
  const cats = block.categories || []
  const btn = (primary: boolean): CSSProperties => ({
    padding: '9px 16px', fontSize: '0.85rem', fontWeight: 600, borderRadius: 8, cursor: 'pointer',
    border: primary ? 'none' : '1px solid var(--pg-border)',
    background: primary ? 'var(--pg-primary)' : 'transparent',
    color: primary ? 'var(--pg-on-primary, #fff)' : 'var(--pg-text)',
  })
  return (
    <div className={`pg-consent pg-consent-${pos}`} role="dialog" aria-label="Cookie notice">
      <div className="pg-consent-msg">
        {s(block.message)}
        {block.policyHref && (
          <>
            {' '}
            <a href={resolveHref(block.policyHref)} style={{ color: 'var(--pg-link)' }}>
              {s(block.policyLabel) || 'Privacy policy'}
            </a>
          </>
        )}
      </div>
      {cats.length > 0 && (
        <div className="pg-consent-cats">
          {cats.map((c) => (
            <label key={c.id} className="pg-consent-cat">
              <input
                type="checkbox"
                data-pg-cat={c.id}
                defaultChecked={c.required || c.defaultOn}
                disabled={c.required}
                required={c.required}
              />
              <span>
                <b>{s(c.label)}</b>
                {c.description ? <span style={{ display: 'block', opacity: 0.75 }}>{s(c.description)}</span> : null}
              </span>
            </label>
          ))}
        </div>
      )}
      <div className="pg-consent-actions">
        {cats.length > 0 && (
          <button type="button" data-pg-prefs="" style={btn(false)}>
            {s(block.settingsLabel) || 'Preferences'}
          </button>
        )}
        {cats.length > 0 && (
          <button type="button" data-pg-save="" style={btn(false)}>Save choices</button>
        )}
        <button type="button" data-pg-decline="" style={btn(false)}>
          {s(block.declineLabel) || 'Decline'}
        </button>
        <button type="button" data-pg-accept="" style={btn(true)}>
          {s(block.acceptLabel) || 'Accept'}
        </button>
      </div>
    </div>
  )
}

// ─── Page chrome (back-to-top, scroll progress) ───────────────────────

/**
 * The floating page affordances. Rendered once per page by each surface's
 * host; `pageChromeScript` drives both. They are inert markup until that
 * script runs, so a failure leaves nothing visibly broken.
 */
export function PageChromeRenderer({ chrome }: {
  chrome: { backToTop?: boolean; backToTopAfter?: number; backToTopPosition?: string
            scrollProgress?: boolean; scrollProgressColor?: string; scrollProgressHeight?: number } | undefined
}) {
  if (!chrome) return null
  const corner = chrome.backToTopPosition === 'bottom-left' ? 'bl' : 'br'
  return (
    <>
      {chrome.scrollProgress && (
        <div
          className="pg-progress"
          aria-hidden
          style={{
            ['--pg-progress-color' as never]: chrome.scrollProgressColor || 'var(--pg-primary)',
            ['--pg-progress-h' as never]: `${chrome.scrollProgressHeight ?? 3}px`,
          }}
        />
      )}
      {chrome.backToTop && (
        <button
          type="button"
          className={`pg-to-top pg-to-top-${corner}`}
          data-after={chrome.backToTopAfter ?? 600}
          aria-label="Back to top"
        >
          <PageIcon name="arrow-up" size={20} />
        </button>
      )}
    </>
  )
}

// ─── Dark-mode toggle ─────────────────────────────────────────────────

/**
 * Light / dark switch for a published page.
 *
 * Three-state on purpose, matching how the CSS is written: no stored choice
 * means "follow the system", and an explicit choice PINS light or dark. A
 * two-state toggle can only ever turn dark on — it has no way to say "go back
 * to following my OS", which is what a visitor who toggled by accident wants.
 *
 * The `data-pg-theme` attribute it stamps on `<html>` is the same hook
 * `darkModeCss` keys its rules off, and `darkModeBootScript` applies the
 * stored value before first paint so there is no white flash on load.
 */
export function DarkModeToggle({ storageKey = 'pg-theme', floating }: {
  storageKey?: string
  floating?: boolean
}) {
  const [mode, setMode] = useState<'system' | 'light' | 'dark'>('system')

  React.useEffect(() => {
    try {
      const v = localStorage.getItem(storageKey)
      if (v === 'light' || v === 'dark') setMode(v)
    } catch { /* storage can be blocked; fall back to following the system */ }
  }, [storageKey])

  const apply = (next: 'system' | 'light' | 'dark') => {
    setMode(next)
    const root = document.documentElement
    try {
      if (next === 'system') { localStorage.removeItem(storageKey); root.removeAttribute('data-pg-theme') }
      else { localStorage.setItem(storageKey, next); root.setAttribute('data-pg-theme', next) }
    } catch {
      // Storage denied — still apply for this page view so the click does
      // something, it just won't persist.
      if (next === 'system') root.removeAttribute('data-pg-theme')
      else root.setAttribute('data-pg-theme', next)
    }
  }

  // Cycle system → dark → light → system, so every state is reachable with
  // repeated taps on one control.
  const next = mode === 'system' ? 'dark' : mode === 'dark' ? 'light' : 'system'
  const icon = mode === 'dark' ? 'moon' : mode === 'light' ? 'sun' : 'monitor'
  const label = `Theme: ${mode}. Switch to ${next}.`

  return (
    <button
      type="button"
      onClick={() => apply(next)}
      aria-label={label}
      title={label}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 38, height: 38, borderRadius: 10, cursor: 'pointer',
        border: '1px solid var(--pg-border)', background: 'var(--pg-surface)',
        color: 'var(--pg-text)',
        ...(floating
          ? { position: 'fixed', right: 24, bottom: 80, zIndex: 880, borderRadius: '50%', boxShadow: '0 8px 24px rgba(0,0,0,.18)' }
          : {}),
      }}
    >
      <PageIcon name={icon} size={18} />
    </button>
  )
}

/** Site-level announcement strip. Hidden until `announcementScript` runs. */
export function AnnouncementRenderer({ bar }: {
  bar: { text: string; href?: string; linkLabel?: string; dismissible?: boolean
         background?: string; textColor?: string; id?: string } | undefined
}) {
  const { resolveHref } = useSiteLink()
  if (!bar || !bar.text) return null
  return (
    <div
      className="pg-announce"
      data-id={bar.id || 'default'}
      hidden
      style={{ background: bar.background || 'var(--pg-primary)', color: bar.textColor || 'var(--pg-on-primary, #fff)' }}
    >
      <span>{bar.text}</span>
      {bar.href && (
        <a href={resolveHref(bar.href)}>{bar.linkLabel || 'Learn more'}</a>
      )}
      {bar.dismissible !== false && (
        <button type="button" data-pg-dismiss="" aria-label="Dismiss">×</button>
      )}
    </div>
  )
}
